import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../../services/apiService";
import type { BoardResponse } from "./boardTypes";

/** The board is live data: refresh it every few minutes and when the tab regains focus. */
const REFRESH_MS = 5 * 60 * 1000;

/**
 * Loads GET /dashboard/board for a window (YYYY-MM-DD, inclusive).
 * `duplicates`: the "Show duplicates" checkbox; the API then also returns the shows
 * marked as duplicate (hidden by default).
 */
export function useShowBoard(from: string, to: string, enabled = true, duplicates = false) {
  const [data, setData] = useState<BoardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const mine = ++seq.current;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { response, error: reqError } = await apiRequest({
          method: "GET",
          url: "dashboard/board",
          params: duplicates ? { from, to, duplicates: "true" } : { from, to },
          data: null,
          isToken: true,
        });
        if (cancelled || mine !== seq.current) return;
        if (reqError || response?.status !== 200) {
          throw new Error(
            reqError?.response?.data?.message || "Could not load the shows",
          );
        }
        setData((response?.data?.data ?? null) as BoardResponse | null);
      } catch (e) {
        if (cancelled || mine !== seq.current) return;
        setError(e instanceof Error ? e.message : "Could not load the shows");
      } finally {
        if (!cancelled && mine === seq.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [from, to, enabled, tick, duplicates]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(refetch, REFRESH_MS);
    const onFocus = () => {
      if (document.visibilityState === "visible") refetch();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [enabled, refetch]);

  return { data, loading, error, refetch };
}
